import format from 'date-fns/format';

import { GlobalsEnum } from 'components/molecules/globals';

import copyToClipboard from 'utils/clipboard';
import { currency } from 'utils/formats';

import { useUI } from 'providers/ui';

const getQuoteUrl = (id: string) =>
  `https://cosmetic.college/enrollment/quote/${id}`;

export type LeadQuoteRow = {
  id: string;
  created_at: string;
  total: number;
  status: string;
  metadata?: Array<{ title?: string; id?: string }> | null;
  user?: {
    id: string;
    given_name?: string;
    family_name?: string;
    email?: string;
  } | null;
};

type LeadQuotesPanelProps = {
  quotes: LeadQuoteRow[];
  loading?: boolean;
  onResend: (id: string) => Promise<void>;
};

export default function LeadQuotesPanel({
  quotes,
  loading,
  onResend,
}: LeadQuotesPanelProps) {
  const ui = useUI();

  function copyQuoteLink(id: string) {
    copyToClipboard(getQuoteUrl(id));
    ui.setToast(true, {
      type: 'success',
      title: 'Quote link copied',
      message: 'The learner quote link is on your clipboard.',
    });
  }

  function openQuoteLink(id: string) {
    window.open(getQuoteUrl(id), '_blank', 'noopener,noreferrer');
  }

  function checkout(quote: LeadQuoteRow) {
    ui.setGlobal(true, GlobalsEnum.newEnrollment, {
      courses: quote.metadata,
      user: quote.user,
    });
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500">
        Loading quotes...
      </div>
    );
  }

  if (!quotes.length) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
        No quotes yet. Send a quote to move this opportunity toward booking.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {quotes.map((quote) => {
        const courseTitles = Array.isArray(quote.metadata)
          ? quote.metadata
              .map((course) => course?.title)
              .filter(Boolean)
              .join(', ')
          : '';

        return (
          <div
            key={quote.id}
            className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">
                  {currency(quote.total)}
                  <span className="ml-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                    {quote.status}
                  </span>
                </p>
                <p className="text-xs text-gray-500">
                  {format(new Date(quote.created_at), 'dd MMM yyyy HH:mm')}
                </p>
                {courseTitles ? (
                  <p className="mt-1 truncate text-xs text-gray-600">
                    {courseTitles}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => copyQuoteLink(quote.id)}
                >
                  Copy link
                </button>
                <button
                  type="button"
                  className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => openQuoteLink(quote.id)}
                >
                  Open
                </button>
                <button
                  type="button"
                  className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => onResend(quote.id)}
                >
                  Resend
                </button>
                <button
                  type="button"
                  className="rounded-md border border-teal-600 bg-teal-50 px-2 py-1 text-xs font-medium text-teal-800 hover:bg-teal-100"
                  onClick={() => checkout(quote)}
                >
                  Checkout
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
